import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  Flex,
  Popover,
  PopoverButton,
  PopoverContainer,
  Text,
  spaces,
} from '@electricjs/arc';
import { useAppSelector } from '@/redux/store';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import { useUpdateMessageFeedbackMutation } from '@/redux/slices/gigawattBackendApiSlice';
import {
  CHAT_NAMESPACES,
  FEEDBACK_REASONS,
  FeedbackReason,
  MessageFeedbackEntry,
} from '@/types/aiChat';
import likeIcon from '@/assets/like.svg';
import dislikeIcon from '@/assets/dislike.svg';

const DEFAULT_FEEDBACK: MessageFeedbackEntry = { rating: null, reasons: [] };

/**
 * Bridges Reakit's own dismiss behavior (Escape/outside click/blur) — which
 * flips the Popover's internal `visible` state — back into our controlled
 * `popoverVisible` state. Implemented as a separate component so the sync
 * happens in an effect rather than during the Popover's render-prop callback.
 */
const PopoverDismissSync = ({
  reakitVisible,
  onDismiss,
}: {
  reakitVisible: boolean;
  onDismiss: () => void;
}) => {
  useEffect(() => {
    if (!reakitVisible) {
      onDismiss();
    }
  }, [reakitVisible, onDismiss]);

  return null;
};

const DOWNVOTE_REASONS = [
  {
    value: FEEDBACK_REASONS.IncorrectInformation,
    labelKey: 'feedback.reasonIncorrectInformation',
  },
  {
    value: FEEDBACK_REASONS.DidNotAnswer,
    labelKey: 'feedback.reasonDidNotAnswer',
  },
  {
    value: FEEDBACK_REASONS.WrongAction,
    labelKey: 'feedback.reasonWrongAction',
  },
] as const;

type MessageFeedbackActionsProps = {
  messageId: string;
  disabled?: boolean;
};

/**
 * Per-message thumbs up/down feedback control for Gigawatt AI chat messages.
 *
 * Redux (messageFeedbackSlice) is the single source of truth for the committed
 * rating/reasons — it survives history restore and stays consistent with the
 * optimistic-update/rollback flow below. Local state here is only for
 * popover-local ephemera (open/closed, draft checked reasons).
 *
 * Note on the down-vote popover: Arc's `PopoverContainer` seeds its `visible`
 * prop into a Reakit "sealed" initial state — it is only read once, on mount,
 * and ignored on subsequent re-renders. Passing a live-updating boolean as
 * `visible` therefore cannot reactively re-open a popover that's already
 * mounted. To keep our own `popoverVisible` state authoritative (so Submit can
 * force-close deterministically, and dismiss can be observed reliably), the
 * whole `PopoverContainer` subtree is mounted/unmounted based on it, seeding
 * `visible={true}` at mount time so Reakit's own Escape/outside-click dismiss
 * behavior initializes correctly.
 */
export const MessageFeedbackActions = ({
  messageId,
  disabled = false,
}: MessageFeedbackActionsProps) => {
  const { t } = useTranslation('ai-chat');
  const { showErrorToast } = useGlobalUI();
  const organizationId = useGetOrganizationId();
  const [updateMessageFeedback] = useUpdateMessageFeedbackMutation();

  const conversationId = useAppSelector(
    state =>
      state.copilotKitChat.conversations[CHAT_NAMESPACES.GIGAWATT_COPILOTKIT]
        ?.conversationId
  );

  // A just-streamed message's id may still be its temporary/live id — idMap
  // resolves it to the persisted id once MESSAGE_IDS_PERSISTED has landed
  // (see useCopilotKitAgent.ts), without ever mutating CopilotKit's own
  // messages array (that was found to trigger CopilotKit's internal
  // "message changed" handling and crash).
  const resolvedMessageId = useAppSelector(
    state => state.messageFeedback.idMap[messageId] ?? messageId
  );

  const { rating } = useAppSelector(
    state =>
      state.messageFeedback.feedback[resolvedMessageId] ?? DEFAULT_FEEDBACK
  );

  // A message with no confirmed persisted id yet (streaming still in
  // flight, or the id-mapping was skipped due to a cardinality mismatch)
  // must stay non-interactive — otherwise feedback could be submitted
  // against an id the backend never persisted.
  const isResolved = useAppSelector(
    state => !!state.messageFeedback.resolvedIds[resolvedMessageId]
  );
  const isDisabled = disabled || !isResolved;

  const [popoverVisible, setPopoverVisible] = useState(false);
  const [checkedReasons, setCheckedReasons] = useState<Set<FeedbackReason>>(
    new Set()
  );

  const downvoteTriggerRef = useRef<HTMLButtonElement>(null);
  const isMountedRef = useRef(false);

  // Return focus to the down-vote trigger whenever the popover closes,
  // whether via Submit or a Reakit-driven dismiss (Escape/outside click/blur).
  // Skipped on mount since popoverVisible starts false and there's nothing to
  // return focus from yet.
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    if (!popoverVisible) {
      downvoteTriggerRef.current?.focus();
    }
  }, [popoverVisible]);

  const submitFeedback = async (
    nextRating: MessageFeedbackEntry['rating'],
    nextReasons: FeedbackReason[]
  ) => {
    // Guard before any optimistic dispatch: without a conversationId there's
    // no API call to make, so committing the optimistic state here would
    // show success in the UI while nothing was actually persisted.
    if (!conversationId) {
      showErrorToast({
        id: `message-feedback-error-${resolvedMessageId}`,
        message: t('feedback.errorToast'),
      });
      return;
    }

    // Optimistic update and rollback-on-failure are handled by
    // updateMessageFeedback's onQueryStarted, so this just needs to
    // surface the error toast.
    try {
      await updateMessageFeedback({
        organizationId,
        conversationId,
        messageId: resolvedMessageId,
        rating: nextRating,
        reasons: nextReasons,
      }).unwrap();
    } catch {
      showErrorToast({
        id: `message-feedback-error-${resolvedMessageId}`,
        message: t('feedback.errorToast'),
      });
    }
  };

  const handleThumbsUpClick = () => {
    if (isDisabled) return;
    setPopoverVisible(false);
    void submitFeedback(rating === 'up' ? null : 'up', []);
  };

  const handleThumbsDownClick = () => {
    if (isDisabled) return;
    if (rating === 'down') {
      void submitFeedback(null, []);
      return;
    }
    setCheckedReasons(new Set());
    setPopoverVisible(true);
  };

  const handleReasonToggle = (value: FeedbackReason) => {
    setCheckedReasons(previous => {
      const next = new Set(previous);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const handleSubmit = () => {
    void submitFeedback('down', [...checkedReasons]);
    setPopoverVisible(false);
  };

  const handleDismiss = () => {
    setPopoverVisible(false);
  };

  const isUpActive = rating === 'up';
  const isDownActive = rating === 'down';

  const downvoteButtonProps = {
    id: `message-feedback-down-${messageId}`,
    ref: downvoteTriggerRef,
    variant: 'text' as const,
    intent: isDownActive ? ('danger' as const) : ('neutral' as const),
    iconBefore: dislikeIcon,
    disabled: isDisabled,
    'aria-label': t(
      isDownActive
        ? 'feedback.thumbsDownActiveLabel'
        : 'feedback.thumbsDownLabel'
    ),
    'aria-pressed': isDownActive,
  };

  return (
    <Flex gap={spaces.space_2xs_rem}>
      <Button
        id={`message-feedback-up-${messageId}`}
        variant="text"
        intent={isUpActive ? 'success' : 'neutral'}
        iconBefore={likeIcon}
        disabled={isDisabled}
        onClick={handleThumbsUpClick}
        aria-label={t(
          isUpActive ? 'feedback.thumbsUpActiveLabel' : 'feedback.thumbsUpLabel'
        )}
        aria-pressed={isUpActive}
      />

      {popoverVisible ? (
        <PopoverContainer visible placement="top" hideAfter={0}>
          <PopoverButton
            as={Button}
            {...downvoteButtonProps}
            onClick={(event: React.MouseEvent) => {
              // Reakit's disclosure toggles its own internal visibility on
              // click; we drive open/close entirely through popoverVisible,
              // so suppress that default behavior here.
              event.preventDefault();
            }}
          />
          <Popover aria-label={t('feedback.reasonsTitle')}>
            {popover => (
              <>
                <PopoverDismissSync
                  reakitVisible={popover.visible}
                  onDismiss={handleDismiss}
                />
                <Flex
                  flexDirection="column"
                  gap={spaces.space_xs_rem}
                  p={spaces.space_s_rem}>
                  <Text variant="label-large">
                    {t('feedback.reasonsTitle')}
                  </Text>
                  {DOWNVOTE_REASONS.map(reason => (
                    <Flex
                      key={reason.value}
                      vAlignContent="center"
                      gap={spaces.space_2xs_rem}>
                      <Checkbox
                        id={`message-feedback-reason-${messageId}-${reason.value}`}
                        checked={checkedReasons.has(reason.value)}
                        onClick={() => handleReasonToggle(reason.value)}
                      />
                      <Text>{t(reason.labelKey)}</Text>
                    </Flex>
                  ))}
                  <Button
                    id={`message-feedback-submit-${messageId}`}
                    disabled={checkedReasons.size === 0}
                    onClick={handleSubmit}>
                    {t('feedback.submit')}
                  </Button>
                </Flex>
              </>
            )}
          </Popover>
        </PopoverContainer>
      ) : (
        <Button {...downvoteButtonProps} onClick={handleThumbsDownClick} />
      )}
    </Flex>
  );
};
